/**
 * Director & holder notices — run the M5c resolution jobs on demand. The
 * director job retries ambiguous and unmatched rows (board matching is
 * database-only and never creates from them), so run it after merging two
 * person records; the holder job takes rows never attempted or parked for
 * an issuer link. Dry run first; counts are what the run did or would do.
 */
import { useState } from 'react';
import { Users, Eye, Play, Loader2 } from 'lucide-react';
import { runNoticeResolution, type NoticeJob, type NoticeResolutionResult } from '../lib/api/notice-resolution.js';

const JOBS: { job: NoticeJob; name: string; detail: string }[] = [
  { job: 'resolve-director-trades', name: 'Director interest notices',
    detail: 'Appendix 3X/3Y/3Z → the board person. Retries ambiguous and unmatched rows — run after a person merge.' },
  { job: 'resolve-holder-filings', name: 'Substantial holder notices',
    detail: 'Forms 603/604/605 → holder, dated holding and associates. Takes rows not yet attempted or parked for an issuer link; may research body-extracted holders.' },
];

interface State { running?: 'dry' | 'apply'; result?: NoticeResolutionResult; mode?: 'dry' | 'apply'; error?: string; done?: number }

export function NoticeResolutionPanel() {
  const [state, setState] = useState<Record<string, State>>({});
  const busy = Object.values(state).some(s => s.running);

  const run = async (job: NoticeJob, mode: 'dry' | 'apply', name: string) => {
    if (mode === 'apply' && !window.confirm(`Resolve ${name.toLowerCase()} into Cynapse?\n\nRun the dry run first to see the counts.`)) return;
    setState(s => ({ ...s, [job]: { running: mode } }));
    try {
      const result = await runNoticeResolution(job, mode === 'dry', st =>
        setState(s => ({ ...s, [job]: { ...s[job], done: st.progress?.done } })));
      setState(s => ({ ...s, [job]: { result, mode } }));
    } catch (err: any) {
      setState(s => ({ ...s, [job]: { error: err?.response?.data?.detail || err?.message || 'Request failed' } }));
    }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 text-grey-500"><Users className="h-5 w-5" /></div>
        <div>
          <h3 className="font-semibold text-grey-900">Director &amp; holder notices</h3>
          <p className="text-sm text-grey-600 mt-1">
            Resolves directors&apos; interest notices and substantial-holder notices into people, companies and dated
            holdings. New filings resolve as they arrive; run these after a person merge, an issuer link repair, or to
            pick up rows that were waiting. Durable jobs — a deploy does not lose them.
          </p>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {JOBS.map(j => {
          const s = state[j.job] || {};
          return (
            <div key={j.job} className="rounded-lg border border-grey-100 p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="text-grey-800">{j.name}</div>
                  <div className="text-xs text-grey-400">{j.detail}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => run(j.job, 'dry', j.name)} disabled={busy}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50">
                    {s.running === 'dry' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Dry run
                  </button>
                  <button onClick={() => run(j.job, 'apply', j.name)} disabled={busy}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
                    {s.running === 'apply' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Apply
                  </button>
                </div>
              </div>
              {s.running && <p className="mt-2 text-xs text-grey-500">Running{s.done ? ` — ${s.done} rows so far` : ''}…</p>}
              {s.error && <p className="mt-2 text-xs text-red-700">{s.error}</p>}
              {s.result && (
                <div className="mt-2 rounded bg-grey-50 px-3 py-2 text-xs text-grey-700">
                  <span className="font-medium text-grey-800">{s.mode === 'dry' ? 'Dry run' : 'Applied'}:</span>{' '}
                  {s.result.rows} rows · {Object.entries(s.result.counts).map(([k, v]) => `${k} ${v}`).join(' · ') || 'nothing to do'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
